import { useEffect, useRef, type RefObject } from "react";

// The floating preview pane sticks to the top of the scrolling workspace
// body, so none of its ancestors has a definite height to size it against
// (the panel group grows with the form column instead). Mirror the
// scrollport's content height into a CSS variable the pane reads, which also
// gives the paper's fit-to-height measure a real number to work with.
//
// Reading the observed content box (never clientHeight) keeps the body's
// page padding out of the value, so the pane ends exactly at the visible
// bottom. The variable can't feed back into the measure: the body's own
// height comes from the workspace flex column, not from its contents.
export function usePreviewViewportHeight(): RefObject<HTMLDivElement | null> {
    const bodyRef = useRef<HTMLDivElement>(null);

    // No dependency list: the drawer portals its content in only after its
    // own first commit, so on a mount-only effect the body would not exist
    // yet and the variable would never be written — the pane would keep its
    // 100% fallback (the full form column height) and the paper would only
    // ever be width-fitted. Reattaching per commit is cheap.
    useEffect(() => {
        const body = bodyRef.current;
        if (!body) {
            return undefined;
        }

        const syncHeight = (contentHeight: number) => {
            if (contentHeight > 0) {
                body.style.setProperty(
                    "--invoice-preview-height",
                    `${contentHeight}px`,
                );
            }
        };

        const style = getComputedStyle(body);
        syncHeight(
            body.clientHeight -
                parseFloat(style.paddingTop) -
                parseFloat(style.paddingBottom),
        );

        const observer = new ResizeObserver((entries) => {
            const contentHeight = entries[0]?.contentRect.height;
            if (contentHeight !== undefined) {
                syncHeight(contentHeight);
            }
        });
        observer.observe(body);
        return () => observer.disconnect();
    });

    return bodyRef;
}
